import React, { useCallback, useEffect, useId, useRef } from 'react';
import type { IframeScenario } from './settings/iframes-mapping';
import { useIframeStylesHook } from './hooks/useIframeStylesHook';
import { useIframePerfEvents } from './hooks/useIframePerfEvents';
import { trace } from 'owa-trace';
import { useIframeState } from './hooks/useIframeState';
import {
    type MemoryNotification,
    useIframeMemoryManagement,
} from './hooks/useIframeMemoryManagement';
import {
    addOnIframeCreatedV2EventHandler,
    addOnIframeDestroyedV2EventHandler,
    removeOnIframeCreatedV2EventHandler,
    removeOnIframeDestroyedV2EventHandler,
    type FrameInfoV2,
} from '@microsoft/wv2-outlook/instanceIframeTracker';
import { useIframeSettings, setUserInteractionWithScenario } from './hooks/useIframeSettings';
import { setIframeInfo } from './utils/getIframeInfo';
import {
    computeMemoryFromFrameInfos,
    updateCacheFromFrameInfos,
    getIframeFullInfo,
} from './utils/iframeMemoryService';
import { prettyPrintFrameInfos } from './utils/tracing';

export type OWAIframeProps = React.HTMLAttributes<HTMLDivElement> & {
    src: string;
    isVisible: boolean;
    scenario: IframeScenario;
    component: (
        containerId: string,
        isVisible: boolean,
        isPreloadRequested: boolean
    ) => React.ReactNode;
    targetWindow?: Window;
    onEvictFromCacheCallback?: (
        processId: number | undefined, // TODO: make non-optional when iframewrapper integrates changes from NH
        sessionIds: string[] | undefined, // TODO: make non-optional when iframewrapper integrates changes from NH
        currentMemoryUsageInMb?: number,
        currentFreeMemoryInMb?: number
    ) => void;
    onHighMemoryEventCallback?: (
        notificationType: MemoryNotification,
        processId: number,
        sessionIds: string[],
        memoryUsageInMb?: number
    ) => void;
};

/**
 * This component should only be used in cases where we can't render the iframe ourselves.
 * For example, MetaOsAppContainer should use this as we can't access the iframe directly.
 * It will manage the iframe view state and track the iframe's performance effects.
 * Use "OWAIframe" for "iframes" we can render ourselves.
 * --> See README.md for more information.
 */
const OWAIframeWrapperInternal: React.FC<OWAIframeProps> = ({
    src,
    isVisible,
    scenario,
    component,
    targetWindow,
    style,
    onEvictFromCacheCallback,
    onHighMemoryEventCallback,
    ...props
}) => {
    const uniqueId = useId();
    const containerId = `cacheable-iframe-container-${scenario}-${uniqueId}`;
    const { enablePreloading, enableCaching } = useIframeSettings(scenario, targetWindow);
    setIframeInfo(scenario, 'isPreloadingEnabled', enablePreloading);
    setIframeInfo(scenario, 'isCachingEnabled', enableCaching);
    const divRef = useRef<HTMLDivElement>(null);
    const { getPerfEvent } = useIframePerfEvents('wrapper', scenario);
    const nativeHostMemoryValue = useRef<number | undefined>(undefined);
    const trackedIframeIdsRef = useRef<Set<string>>(new Set());
    const listenersMapRef = useRef<Map<string, () => void>>(new Map());
    const timeoutsMapRef = useRef<Map<string, number>>(new Map());

    const onIframeIdsChanged = useCallback(() => {
        for (const iframeId of trackedIframeIdsRef.current) {
            const { dpLoadStart, hasStarted } = getPerfEvent(iframeId);
            if (dpLoadStart && !hasStarted) {
                dpLoadStart('initial');
            }
        }
    }, [getPerfEvent, trackedIframeIdsRef.current]);
    const {
        value: state,
        shouldRender,
        isVisibleToUser,
    } = useIframeState(scenario, isVisible, enablePreloading, enableCaching, onIframeIdsChanged);
    const { doNotRenderCachedIframe } = useIframeMemoryManagement(
        state,
        scenario,
        isVisible,
        enableCaching,
        trackedIframeIdsRef.current.size,
        onEvictFromCacheCallback,
        onHighMemoryEventCallback
    );

    const managedStyle = useIframeStylesHook(isVisibleToUser, style);

    trace.info(
        ` iframe wrapper re-render: ${JSON.stringify({
            containerId,
            src,
            isVisible,
            enablePreloading,
            enableCaching,
            state,
            shouldRender,
            isVisibleToUser,
            doNotRenderCachedIframe,
            trackedIframeCount: trackedIframeIdsRef.current.size,
            iframeInfo: getIframeFullInfo(scenario),
        })}`,
        'iframeWrapper'
    );

    // Find all child iframes and set up event listeners for each load event
    useEffect(() => {
        let observer: MutationObserver | null = null;

        if (divRef.current && shouldRender) {
            // Observe the children to find iframes because they aren't always available when the wrapper is rendered
            // If the MetaOsAppContainer exposes an "onload" event, we should use that instead.
            observer = new MutationObserver(mutations => {
                for (const mutation of mutations) {
                    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                        const iframesMap = findIframesByIdMap(mutation.addedNodes.item(0));

                        for (const [iframeId, iframe] of iframesMap) {
                            // Skip if already tracked
                            if (trackedIframeIdsRef.current.has(iframeId)) {
                                continue;
                            }

                            trackedIframeIdsRef.current.add(iframeId);
                            trace.info(` iframe found with id: ${iframeId}`, 'iframeWrapper');

                            const { dpLoadStart, dpLoadEnd, dpEndEvent, hasStarted } =
                                getPerfEvent(iframeId);

                            if (!hasStarted) {
                                dpLoadStart('session');
                            }

                            // Create the on load listener for this specific iframe
                            const listener = () => {
                                trace.info(` iframe loaded with id: ${iframeId}`, 'iframeWrapper');
                                dpLoadEnd(
                                    src,
                                    state,
                                    enablePreloading,
                                    nativeHostMemoryValue.current
                                ).then(() => {
                                    cleanup();
                                });
                            };

                            // Create the function to remove the listener, timeout, and end event
                            const cleanup = () => {
                                dpEndEvent();
                                iframe?.removeEventListener('load', listener);
                                timeoutsMapRef.current.delete(iframeId);
                                listenersMapRef.current.delete(iframeId);
                                nativeHostMemoryValue.current = undefined;
                                trace.info(
                                    ` iframe listener removed for id: ${iframeId}`,
                                    'iframeWrapper'
                                );
                            };

                            listenersMapRef.current.set(iframeId, listener);
                            iframe.addEventListener('load', listener);

                            // If the onload doesn't trigger after 5s, use a timeout to clean up
                            const timeoutId = window.setTimeout(() => {
                                if (listenersMapRef.current.has(iframeId)) {
                                    listener();
                                    trace.warn(
                                        ` iframe load timeout for id: ${iframeId}`,
                                        'iframeWrapper'
                                    );
                                }
                            }, 5000);

                            timeoutsMapRef.current.set(iframeId, timeoutId);
                        }
                    }
                }
            });

            observer.observe(divRef.current, { childList: true, subtree: true });
        }

        return () => {
            // Cleanup the observer and all listeners if the wrapper is unloading
            trace.info(
                ` iframe unloading - shouldRender: ${shouldRender}, trackedIframeCount: ${trackedIframeIdsRef.current.size}`
            );

            observer?.disconnect();

            // Clean up all listeners, timeouts, and pending datapoints
            for (const [iframeId, listener] of listenersMapRef.current) {
                const iframe = document.getElementById(iframeId) as HTMLIFrameElement | null;
                iframe?.removeEventListener('load', listener);

                const timeoutId = timeoutsMapRef.current.get(iframeId);
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }

                const { dp, dpEndEvent } = getPerfEvent(iframeId);
                if (!dp?.hasEnded) {
                    dp?.addCustomData({ abortedBeforeLoad: true });
                    dpEndEvent();
                    trace.info(
                        ` iframe listener cleaned up before load completed for id: ${iframeId}`,
                        'iframeWrapper'
                    );
                }
            }

            listenersMapRef.current.clear();
            timeoutsMapRef.current.clear();
            trackedIframeIdsRef.current.clear();
        };
    }, [divRef.current, shouldRender, state, enablePreloading, src, getPerfEvent]);

    useEffect(() => {
        const createdHandler = async (frameInfos: FrameInfoV2[]) => {
            trace.info(
                ` iframe created event received from native host:\n${prettyPrintFrameInfos(
                    frameInfos,
                    scenario
                )}`,
                'iframeWrapper'
            );
            // Since we have fresh data from native host, update the cache
            updateCacheFromFrameInfos(frameInfos);

            const memoryInfo = computeMemoryFromFrameInfos(scenario, frameInfos);
            nativeHostMemoryValue.current = memoryInfo?.totalMemoryMb;
        };

        const destroyedHandler = async (frameInfos: FrameInfoV2[]) => {
            trace.info(
                ` iframe destroyed event received from native host:\n${prettyPrintFrameInfos(
                    frameInfos,
                    scenario
                )}`,
                'iframeWrapper'
            );
            // Since we have fresh data from native host, update the cache
            updateCacheFromFrameInfos(frameInfos);

            const memoryInfo = computeMemoryFromFrameInfos(scenario, frameInfos);
            nativeHostMemoryValue.current = memoryInfo?.totalMemoryMb;
        };

        const createdListener = addOnIframeCreatedV2EventHandler?.(createdHandler);
        const destroyedListener = addOnIframeDestroyedV2EventHandler?.(destroyedHandler);
        return () => {
            createdListener && removeOnIframeCreatedV2EventHandler?.(createdListener);
            destroyedListener && removeOnIframeDestroyedV2EventHandler?.(destroyedListener);
        };
    }, []);

    trace.info(
        ` iframe wrapper render. shouldRender: ${shouldRender}, doNotRenderCachedIframe: ${doNotRenderCachedIframe}`,
        'iframeWrapper'
    );

    if (isVisibleToUser) {
        setUserInteractionWithScenario(scenario);
    }

    const isPreloadRequested = state === 'PRELOAD-HIDDEN';

    return shouldRender && !doNotRenderCachedIframe ? (
        <div {...props} id={containerId} ref={divRef} style={managedStyle}>
            {component(containerId, isVisibleToUser, isPreloadRequested)}
        </div>
    ) : null;
};

// Recursively search for all iframes with id attributes and return as a Map
function findIframesByIdMap(node: Node | null): Map<string, HTMLIFrameElement> {
    const iframes = new Map<string, HTMLIFrameElement>();

    function traverse(current: Node | null) {
        if (!current) {
            return;
        }

        if (isIframeElement(current)) {
            const id = (current as HTMLIFrameElement).id;
            if (id) {
                iframes.set(id, current as HTMLIFrameElement);
            }
            return;
        }

        for (let i = 0; i < current.childNodes.length; i++) {
            traverse(current.childNodes[i]);
        }
    }

    traverse(node);
    return iframes;
}

function isIframeElement(element?: Node | null): element is HTMLIFrameElement {
    return element?.nodeName === 'IFRAME';
}

// Avoid unnecessary re-renders if the props haven't changed
export const OWAIframeWrapper: React.FC<OWAIframeProps> = React.memo(OWAIframeWrapperInternal);
